/**
 * System-level automation workflow templates (frontend constants).
 * These are the built-in blueprints. Users can "clone" them to create
 * custom copies stored in the backend (AutomationTemplate model).
 *
 * i18n: los `name`/`description`/`label` de este archivo son el **EN canónico**
 * (es lo que se persiste al clonar un starter). El texto que se muestra sale de
 * `pages/automations/automationMessages.ts` (`STARTER_MESSAGES` y
 * `STARTER_CATEGORY_MESSAGES`), indexado por `id` / `value`.
 * El contenido de `suggestedTemplate` es dato de plantilla: no se traduce.
 */

export type AutomationStarterCategory =
  | 'ONBOARDING'
  | 'LEADS'
  | 'POLICIES'
  | 'RETENTION'
  | 'CROSS_SELL'
  | 'BIRTHDAY'

export interface AutomationStarter {
  id: string
  name: string
  description: string
  category: AutomationStarterCategory
  triggerType: string
  triggerConfig: Record<string, any>
  channel: 'EMAIL' | 'SMS'
  delayDays: number
  sendHour: number
  sendMinute: number
  suggestedTemplate?: {
    name: string
    subject?: string
    bodyText: string
    bodyHtml?: string
  }
}

export const AUTOMATION_STARTER_CATEGORIES: { value: AutomationStarterCategory | 'ALL'; label: string }[] = [
  { value: 'ALL',        label: 'All' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'LEADS',      label: 'Leads' },
  { value: 'POLICIES',   label: 'Policies' },
  { value: 'RETENTION',  label: 'Retention' },
  { value: 'CROSS_SELL', label: 'Cross-sell' },
  { value: 'BIRTHDAY',   label: 'Birthday' },
]

const WELCOME_EMAIL_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#1a5276;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">Welcome to {{agency_name}}!</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            Thank you for trusting us with your insurance needs. We're thrilled to have you as a client
            and are committed to making sure you have the right coverage for you and your family.
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            If you have any questions or need assistance, don't hesitate to reach out — we're here for you.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#1a5276;border-radius:6px;">
            <a href="{{client_center_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Access Your Client Center
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

const CROSS_SELL_HOME_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#1a6b3c;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">🏠 Protect Your Home Too</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            You're already covered with your auto policy — great choice! Did you know bundling your
            <strong>home insurance</strong> with us can save you up to <strong>20%</strong> on both policies?
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            Let us put together a quick home insurance quote for you. It only takes a few minutes
            and there's no obligation.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#1a6b3c;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Get My Home Quote
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

const CROSS_SELL_AUTO_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#1a4070;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">🚗 Add Auto Coverage Today</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            Your home is protected — now let's make sure your vehicle is too. Bundle your
            <strong>auto insurance</strong> with your existing home policy and enjoy multi-policy savings.
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            Our agents can find you competitive rates in minutes. Reach out and let's get you covered.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#1a4070;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Get My Auto Quote
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

const LEAD_FOLLOWUP_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#7d6608;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">Still Here for You</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            We noticed it's been a while since we last connected, and we wanted to check in.
            Finding the right insurance coverage is important, and we'd love to help you take the next step.
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            Our team is ready to answer any questions and put together options that fit your needs and budget.
            No pressure — just a quick conversation.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#7d6608;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Let's Talk
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

const CANCELLATION_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#922b21;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">⚠️ Important: Policy Notice</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            We've noticed a change in the status of your policy and want to make sure you stay protected.
            Gaps in coverage can leave you exposed to significant financial risk.
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            Please contact us as soon as possible so we can review your options and find the best solution
            to keep your coverage active.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#922b21;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Contact Us Now
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

const LEAD_STAGE_HTML = `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:30px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">
        <tr><td style="background:#1f618d;padding:28px 40px;text-align:center;">
          <h1 style="color:#ffffff;margin:0;font-size:24px;">A Quick Update From Us</h1>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="color:#333;font-size:16px;margin:0 0 16px;">Hi {{first_name}},</p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 20px;">
            We wanted to follow up and let you know that your insurance application is progressing.
            Our team is working to ensure you get the best coverage at the most competitive rate.
          </p>
          <p style="color:#555;font-size:15px;line-height:1.6;margin:0 0 28px;">
            If you have any questions about where things stand or what comes next, please don't hesitate to reach out.
            We're happy to walk you through the process.
          </p>
          <table cellpadding="0" cellspacing="0"><tr><td style="background:#1f618d;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:12px 28px;color:#ffffff;font-size:15px;font-weight:bold;text-decoration:none;">
              Check Your Status
            </a>
          </td></tr></table>
        </td></tr>
        <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #eee;">
          <p style="color:#888;font-size:13px;margin:0;">
            {{agency_name}} · {{agency_phone}}<br>
            <a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Unsubscribe</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim()

export const AUTOMATION_STARTERS: AutomationStarter[] = [
  {
    id: 'system-welcome-new-customer',
    name: 'Welcome Your New Customers',
    description: 'Send a warm welcome email when a policy is completed. Perfect for onboarding new clients.',
    category: 'ONBOARDING',
    triggerType: 'CONDITIONAL_POLICY',
    triggerConfig: { condition_rule: 'POLICY_COMPLETED', duration_days: 0 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 9,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Welcome New Customer',
      subject: 'Welcome to {{agency_name}}, {{first_name}}!',
      bodyText: 'Hi {{first_name}},\n\nThank you for trusting us with your insurance needs. We\'re thrilled to have you as a client.\n\nIf you have any questions, don\'t hesitate to reach out.\n\n{{agency_name}}',
      bodyHtml: WELCOME_EMAIL_HTML,
    },
  },
  {
    id: 'system-birthday-wishes',
    name: 'Send Customer Birthday Wishes',
    description: 'Automatically send a birthday greeting to your clients on their special day.',
    category: 'BIRTHDAY',
    triggerType: 'HAPPY_BIRTHDAY',
    triggerConfig: {},
    channel: 'SMS',
    delayDays: 0,
    sendHour: 9,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Birthday Wishes',
      bodyText: '🎂 Happy Birthday, {{first_name}}! Wishing you a wonderful day! From everyone at {{agency_name}}.',
    },
  },
  {
    id: 'system-cross-sell-home',
    name: 'Cross-sell: Add Home Insurance',
    description: 'Reach out to clients with auto coverage who may benefit from adding home insurance.',
    category: 'CROSS_SELL',
    triggerType: 'CONDITIONAL_POLICY',
    triggerConfig: { condition_rule: 'CROSS_SELL_HOME', duration_days: 30 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 10,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Cross-sell: Auto → Home',
      subject: 'Protect your home too, {{first_name}}',
      bodyText: 'Hi {{first_name}},\n\nYou\'re already covered with auto insurance — great choice! Did you know bundling home insurance with us can save you up to 20%?\n\nLet us put together a quick quote for you.\n\n{{agency_name}}',
      bodyHtml: CROSS_SELL_HOME_HTML,
    },
  },
  {
    id: 'system-cross-sell-auto',
    name: 'Cross-sell: Add Auto Insurance',
    description: 'Reach out to clients with home coverage who don\'t yet have auto insurance.',
    category: 'CROSS_SELL',
    triggerType: 'CONDITIONAL_POLICY',
    triggerConfig: { condition_rule: 'CROSS_SELL_AUTO', duration_days: 30 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 10,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Cross-sell: Home → Auto',
      subject: 'Get auto coverage today, {{first_name}}',
      bodyText: 'Hi {{first_name}},\n\nYour home is protected — now let\'s make sure your vehicle is too. Bundle auto insurance and enjoy multi-policy savings.\n\nOur agents can find you competitive rates in minutes.\n\n{{agency_name}}',
      bodyHtml: CROSS_SELL_AUTO_HTML,
    },
  },
  {
    id: 'system-lead-30-days',
    name: 'Lead 30-Day Follow-up',
    description: 'Re-engage leads that have been in your pipeline for 30 days without converting.',
    category: 'LEADS',
    triggerType: 'CONDITIONAL_LEAD',
    triggerConfig: { condition_rule: 'LEAD_DURATION', duration_days: 30 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 9,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Lead 30-Day Follow-up',
      subject: 'Still here for you, {{first_name}}',
      bodyText: 'Hi {{first_name}},\n\nWe noticed it\'s been a while since we last connected. Finding the right insurance is important, and we\'d love to help.\n\nReach out whenever you\'re ready — no pressure.\n\n{{agency_name}}',
      bodyHtml: LEAD_FOLLOWUP_HTML,
    },
  },
  {
    id: 'system-lead-stage-followup',
    name: 'Lead Stage Progress Update',
    description: 'Keep leads engaged with a status update when they\'ve been in a pipeline stage for a few days.',
    category: 'LEADS',
    triggerType: 'CONDITIONAL_LEAD',
    triggerConfig: { condition_rule: 'LEAD_STAGE', lead_stage: '', duration_days: 3 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 9,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Lead Stage Follow-up',
      subject: 'A quick update from {{agency_name}}',
      bodyText: 'Hi {{first_name}},\n\nWe wanted to follow up and let you know your application is progressing.\n\nIf you have any questions, please don\'t hesitate to reach out.\n\n{{agency_name}}',
      bodyHtml: LEAD_STAGE_HTML,
    },
  },
  {
    id: 'system-policy-cancellation',
    name: 'Policy Cancellation Review',
    description: 'Alert your team and reach out to clients when a policy status changes — catch cancellations early.',
    category: 'POLICIES',
    triggerType: 'CONDITIONAL_POLICY',
    triggerConfig: { condition_rule: 'STATE_CHANGE', duration_days: 0 },
    channel: 'EMAIL',
    delayDays: 0,
    sendHour: 8,
    sendMinute: 0,
    suggestedTemplate: {
      name: 'Policy Status Change Notice',
      subject: '⚠️ Important: Policy Notice for {{first_name}}',
      bodyText: 'Hi {{first_name}},\n\nWe\'ve noticed a change in the status of your policy. Please contact us as soon as possible so we can review your options and keep your coverage active.\n\n{{agency_name}} · {{agency_phone}}',
      bodyHtml: CANCELLATION_HTML,
    },
  },
]
